import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import track from "../../../utils/track";
import BirthplaceField from "../../custom/birthplace-field/birthplace-field";
import "./ziwei-jingyue-style.scss";
import videoUrl from "../../../utils/video-url";

//—— 鏡月・紫微斗數：開場劇情與問診 ——
//
//  點進產品頁不是先看介紹與價格，而是直接進到一段劇情：
//  她在星圖前等你 → 蒙上雙眼才看得見 → 要你把生辰說給她聽 → 問你要看誰的。
//  「買一份紫微報告」被包裝成「去見一個人」，報生辰才有被問的理由。
//
//  整段走法全在 SCENES 這張表裡，元件只負責「顯示目前這一幕」與「切到下一幕」。
//  之後要加場景、改台詞、調順序都只動那張表，不用碰渲染邏輯。
//
//  場景有兩種型別，看欄位就知道是哪一種：
//    劇情幕（lines / actions）  她說話，你從幾個回應裡挑一個，挑完就往下走
//    問診幕（question / options）你選一個答案，按「下一步」才走 —— 選錯可以改
//  差別不只是版型：劇情的選擇是「當下的反應」，問診的選擇是「要送出的資料」，
//  後者必須能反悔，所以多一顆確認鍵。
//
//  台詞是「一句一句播」而不是整段一次顯示：
//  一句浮現、停一下、往上抽掉，再換下一句，全部播完才出現選項。
//  這是對話的節奏 —— 整段一次攤開就變成讀字幕，不是有人在跟你說話。
//
//  影片是「一幕一支」：jingyue-1 是她在星圖前、jingyue-2 是她開口、
//  jingyue-3 是整段問診停住的那一格。
//  （jingyue-4 與 jingyue-5 不在這裡 —— 那兩幕在故事頁的開場。）
//
//  影片元素刻意「不」放在換幕重掛的那一層裡，而是用 key={scene.visual}：
//  連續幾幕都是同一支影片時它不會重新載入，播放是連續的；
//  換到不同影片時才重掛、從頭播。整段看起來像一鏡到底，而不是每按一次就閃一下。

//—— 影片 ——
//  一幕一支。元件是用 key={scene.visual} 決定要不要重掛影片的：
//  連續幾幕指著同一支就連續播、換支才重掛從頭播。
//
//  第三幕之後的整段問診都用 JINGYUE_VIDEO_3：問診幕把它停住並模糊（見各幕的 blur），
//  停住的就是她說完「我們一起走進你的命宮」的那一格 —— 她在等你報生辰。
//
//  jingyue-1 的路徑在 constant/products.js 也有一份（欄位叫 intro，給首頁預載用）——
//  那邊先把它抓進快取，這裡才不會一進來就卡一張靜止的畫面。改檔名要兩邊一起改。
const JINGYUE_VIDEO_1 = videoUrl("jingyue-1.mp4");
const JINGYUE_VIDEO_2 = videoUrl("jingyue-2.mp4");
const JINGYUE_VIDEO_3 = videoUrl("jingyue-3.mp4");
//  jingyue-4（她讀完盤）與 jingyue-5（她開口）不在這裡 ——
//  那兩幕搬去故事頁的開場了（見 jingyue-story/jingyue-story-intro）。
//  搬的理由是時間：故事頁一掛載就開始生成命書，
//  那兩支影片加上中間要按一次「開始解命」，剛好把生成藏在劇情底下。

//—— 圖片按鈕 ——
//  字已經畫在圖裡了，所以 action 的 label 不再拿來顯示 —— 但仍然要留著，
//  它是這顆按鈕的無障礙名稱（讀屏只讀得到 aria-label，讀不到圖上的字）。
const BTN_ENTER = `${process.env.PUBLIC_URL}/images/jingyue/jingyue-story-btn-1.png`;

//解命影片播完的去處。
//  故事頁是另一支元件、另一條路由 —— 它是可以捲動的長頁，
//  而這一頁是滿版固定高度（overflow: hidden），兩種版型塞不進同一個元件。
const STORY_PATH = "/product/ziwei-jingyue/story";

//一句台詞從浮現到淡掉的總時間，要和 scss 的 jingyue-line 動畫時長一致。
//  改這裡就要改那裡，否則會出現「動畫播完了還卡著」或「字還在就被換掉」。
const LINE_MS = 1500;

//一句台詞有幾個「要讀的字」。
//  強制換行的 \n 不算 —— 它不是字，是排版。算進去的話，斷一次行就多停 100ms。
const readableLength = (text) => String(text || "").replace(/\n/g, "").length;

//一句台詞裡最長的那一行（以強制換行切開）。
//  字級要看的是「最長的一行放不放得下」，不是整句有幾個字 ——
//  一句話斷成兩行之後，每一行都可能短到不必縮字級。
const longestRun = (text) =>
  String(text || "")
    .split("\n")
    .reduce((max, part) => Math.max(max, part.length), 0);

//SCENES 裡的一句台詞，攤成固定的形狀 { text, max }。
//  表裡可以寫純字串（絕大多數的句子），也可以寫 { text, max } ——
//  max 是「這一句的字級上限」，用來喊出一句話（「來吧！」）。
//  給一個上限而不是直接給字級：算式仍然要跑（見 scss 的 jingyue__line），
//  窄螢幕上它還是會自己縮到擺得下，只是不縮到比一般台詞小。
//
//  攤平放在這裡而不是散在各處判斷 typeof：
//  下游有四個地方要用到這一句（算字數、算時長兩次、印出來），
//  每個地方各判斷一次的話，漏掉一個就會印出 "[object Object]"。
const normalizeLine = (line) =>
  typeof line === "string" ? { text: line, max: 0 } : { max: 0, ...line };

//一句台詞要停多久。
//  LINE_MS 那個 1.5 秒是給四五個字的短句用的（「來算命的？」「少廢話！」）——
//  長句照同一個時間播，字還沒讀完就被抽掉了。
//  所以按字數加時間：一個字給 100ms，短句仍然吃 LINE_MS 這個下限。
//
//  hold 是那一幕自己要求的額外停留（SCENES 的 lineHold）——
//  加在算完之後，所以它是純粹的「再多停這麼久」，不受下限影響。
//
//  算出來的值同時要餵給 CSS（animation-duration）與 JS（換句的 timer），
//  作法同 LEAD_IN_MS —— 用 inline style 套上去，不在 scss 另外寫一份。
const lineDuration = (text, hold = 0) =>
  Math.max(LINE_MS, 900 + readableLength(text) * 100) + hold;

//場景淡入完、她開口之前的那段空白。
//  一進場就有字等在那裡，像字卡不像有人說話 —— 留半秒讓畫面先落定。
//  只有第一句要等，後面的句子接著播。
//
//  這個值同時要餵給 CSS（第一句的 animation-delay）與 JS（換句的 timer），
//  所以用 inline style 把它套上去，而不是另外在 scss 寫一個 0.5s ——
//  兩邊各寫一份遲早會分叉。
const LEAD_IN_MS = 500;

//台詞最多等影片交棒多久。超過就不等了，讓故事先走 ——
//  網路爛到這個地步，停在一片黑上不說話比字幕早出來更糟。
const VISUAL_WAIT_MS = 4000;

//畫面出來之後再等多久，底部面板（按鈕與標語）才浮上來。
//  面板不隨台詞走，沒有這一拍的話，影片才剛接上、畫面都還沒讓人看清楚，
//  按鈕就已經壓在下面了 —— 這一幕要先是一個畫面，才是一個選擇。
const PANEL_DELAY_MS = 1000;

//閣樓外那句標語的停留時間 —— 一句停這麼久，然後換下一句。
//  比台詞（LINE_MS）慢得多：台詞是「他正在說話」，一句接一句才有對話的節奏；
//  標語是掛在門口的招牌，換太快會變成跑馬燈，人還沒讀完就沒了。
//  要和 scss 的 jingyue-tagline 動畫時長一致。
const TAGLINE_MS = 3600;

//台詞說完到自動換幕之間的停頓（autoNext 的幕用）。
//  最後一句的淡出已經含在它自己的時間裡，所以這裡只是留一口氣 ——
//  讓畫面空著半秒再切，像他說完話轉身走進去。
//  給太久會變成「是不是卡住了」，畢竟這一幕沒有按鈕可以按。
const AUTO_NEXT_MS = 500;

//起播時要跳過的開頭秒數。
//
//  這幾支影片是拿角色主視覺（public/images/characters/jingyue.jpg 那一類）
//  當第一格生成出來的 —— 第 0 格就是那張靜圖，第 2 格才開始動。
//  量出來的差異很清楚：第 0 格和 0.2 秒差 43，而 0.2 秒到 0.5 秒只差 12。
//
//  照原樣播的後果是：首頁點進來會先看到「卡片上那張圖被放大」停一下，
//  然後畫面跳一下才開始動 —— 看起來像載入失敗，其實是影片自己的開頭。
//
//  所以起播直接從這裡開始，把那一格跳掉。取 0.3 是因為 0.2 秒就已經完全進入
//  動畫狀態了，多留一點餘裕。整段影片十秒，跳掉 0.3 秒看不出來。
const INTRO_SKIP_S = 0.3;

//—— 出生日期的輸入處理 ——
//
//  刻意不用 input[type=date]：那會叫出系統日曆，而填生日是「我知道答案、
//  只想把它打進去」的動作 —— 用日曆要一路往回翻幾十年，比直接打字慢得多。
//  改成數字鍵盤直接輸入，斜線由程式補上。

//把使用者打的東西收成 YYYY/MM/DD。
//  只取數字再重建，所以使用者不管有沒有自己打斜線、在中間刪字，
//  結果都是同一種格式 —— 不必去處理游標位置與既有斜線的各種組合。
const formatBirthDate = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)]
    .filter(Boolean)
    .join("/");
};

//八碼填滿了嗎（只看長度，不管日期合不合理）
const isBirthDateComplete = (value) =>
  /^\d{4}\/\d{2}\/\d{2}$/.test(value || "");

//—— 出生時間 ——
//
//  ⚠️ 鏡月**沒有「不知道時間」這個選項**，白夜有。
//
//  紫微斗數定命宮要先知道時辰 —— 缺了就不是「少一段」，是整張盤排不出來
//  （十二宮全部跟著錯位）。白夜少了時柱還有年月日三柱可以批，這裡不行。
//  所以那顆開關、它的提示字、以及 isBirthTimeValid 裡的例外都拿掉了，
//  讓這一題變成必填 —— 寧可在這裡擋住，也不要讓人付了錢才發現排不出盤。
//  （後端 engines/ziwei-jingyue 的 getChart 也會再擋一次。）

//收成 HH:MM。作法同日期：只取數字再重建
const formatBirthTime = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  return [digits.slice(0, 2), digits.slice(2, 4)].filter(Boolean).join(":");
};

const isBirthTimeComplete = (value) => /^\d{2}:\d{2}$/.test(value || "");

const isBirthTimeValid = (value) => {
  if (!isBirthTimeComplete(value)) return false;

  const [h, m] = value.split(":").map(Number);
  return h <= 23 && m <= 59;
};

//真的是一個存在的日子嗎。
//  用 Date 反推回來比對：2 月 30 日建出來會跳成 3 月 2 日，三個欄位一比就抓得到。
//  單純檢查「月 1-12、日 1-31」是抓不出來的。
const isBirthDateValid = (value) => {
  if (!isBirthDateComplete(value)) return false;

  const [y, m, d] = value.split("/").map(Number);
  if (y < 1900) return false;

  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return false;
  }

  //還沒出生的人不需要算命。比到「日」為止，所以今天出生也算數。
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dt <= today;
};

//—— 劇情表 ——
//  visual    這一幕的畫面（之後換成影片）
//  blur      背景要不要高斯模糊，並且把影片停在那一格 —— 問診幕用，
//            把注意力從畫面收回到問題上（兩件事綁在一起，理由見下方的暫停 effect）
//  lines     台詞序列，一句一句播。省略代表不說話
//            句子裡的 \n 是強制換行（同一句、同一次播，只是分兩行顯示）
//            一句可以寫成 { text, max } 把字級上限拉高（喊話用，見 normalizeLine）
//  actions   劇情幕的回應選項：label（按鈕文字）、next（下一幕）
//            image（可選，改用圖片按鈕；label 轉當無障礙名稱）
//  autoNext  不給選項，台詞說完自己往下走的去處。和 actions 二選一
//  waitVideo 選項要等影片播完才出現（不是台詞播完就出現）。這種幕的影片不循環
//  holdEnd   影片播完就停在最後一格，不回頭重播。
//            跟 waitVideo 的差別是它不碰按鈕的時機 ——
//            「台詞講完就給按鈕，但畫面演完就該停住」是這個
//  lineHold  這一幕的每一句要再多停幾 ms。省略就照字數算（見 lineDuration）
//  leadIn    這一幕開口前要先安靜幾 ms。省略就用 LEAD_IN_MS
//  taglines  掛在選項上方的標語，會自己輪播。省略代表不掛
//  question  問診幕的題目
//  options   問診幕的答案：value（存起來的值）、label（顯示文字）
//  next      問診幕按下「下一步」的去處。給路徑（以 / 開頭）就是離開這一頁
//  skip      這一幕要不要在下方掛「略過故事」
const SCENES = {
  //一幕：星圖前。
  //  白夜站在門口挑釁（「你有膽量看嗎」）；鏡月不挑釁 ——
  //  她的張力來自「我早就看完了，只是在等你來問」。那是一種更安靜的壓迫：
  //  你還沒開口，有人已經把你的一生讀過一遍。
  //
  //  三句輪播講同一件事的三個切面：她已經看完了（不是現在才算）、
  //  她看得細（十二格、一格一格）、她不急（所以你也不用急）。
  gate: {
    visual: JINGYUE_VIDEO_1,
    taglines: [
      "真相擺在眼前，讓我陪著你看",
      "你的紫微星，正忽明忽暗",
      "我講的，都是天上的星為你寫下的劇本",
    ],
    //這一顆用圖片按鈕：字已經畫在圖裡，用文字按鈕做不出那個質感。
    //  label 留著當無障礙名稱。
    actions: [{ label: "請她照給我看", image: BTN_ENTER, next: "enter" }],
  },

  //二幕：進到她面前。
  //  三句遞進：你來了（她知道你會來）→ 這張圖我看了很久（時間感）
  //  → 每一格都還在原地等你（把「命盤」說成一個在等人的東西）。
  //
  //  說完給一顆按鈕而不是自己往下走 —— 見下面 actions 那一段的說明。
  enter: {
    visual: JINGYUE_VIDEO_2,
    lines: ["蒙上雙眼", "方能看清天上星辰…"],
    //慢一點。鏡月的語速本來就比白夜慢 —— 她不催人，
    //  台詞之間的停頓本身就是她的性格。
    lineHold: 600,
    holdEnd: true,
    //  一顆按鈕隔開二三幕（Kevin 2026-10-12 定）。
    //
    //  除了讓「坐不坐下」成為使用者的決定，它還順手解掉一個時序問題：
    //  autoNext 的時候台詞的計時從換幕那一刻就起算，但影片要等新的那一層
    //  載到可播才淡入 —— 量到中間有 0.3 秒是「莫怕！」配著上一支畫面。
    //  有按鈕的話，按下去的那一刻就開始換片，台詞是按完才起算，不會錯開。
    actions: [{ label: "靜靜坐下", next: "meet" }],
  },

  //三幕：接續二幕，戲到這裡收尾。
  //  三句是一段完整的話：先安撫（不必怕）→ 給出她的世界觀（萬物皆有定數）
  //  → 收在一個請求（把生辰說給我聽）。
  //
  //  —— 為什麼沒有驚嘆號、也不用命令式 ——
  //  鏡月的設定是「溫婉、空靈、超脫，語氣輕柔，彷彿聲音是從星空中傳來」
  //  （見 constant/products.js 的 persona.tone，那一行是語氣的唯一基準）。
  //  原本寫的是「莫怕！」與「將你的生辰報上來吧！」，兩句都帶力道 ——
  //  那比較像白夜或衙門，不是她。她不催、不喊，只是把話放在那裡。
  //
  //  「說給我聽」而不是「寫下來」「報上來」：她蒙著眼，
  //  接收的動作本來就是聽 —— 這一個字把角色設定接回台詞裡。
  //
  //  最後那句是交棒：她講完了，接著要動的人是你，所以下一幕直接進問診。
  //
  //  ⚠️ 下一輪問診的「時辰」那一題**沒有「不知道」可選**（紫微缺時辰定不出
  //  命宮，見檔案上方 TIME_UNKNOWN 那段）。白夜是在這一幕的台詞裡先講明白的，
  //  鏡月的台詞沒有提 —— 所以那個理由改寫在那一題自己的 note 上。
  //
  //  autoNext：這段話沒有分岔，插一顆按鈕只是多一次點擊。
  meet: {
    visual: JINGYUE_VIDEO_3,
    lines: ["不必怕", "萬物皆有定數", "把生辰說給我聽"],
    lineHold: 600,
    holdEnd: true,
    autoNext: "relation",
  },

  //—— 問診 ——
  //  從這裡開始畫面停住並模糊（blur），把注意力從影片收回到問題上。
  //  題目與選項沿用白夜那一套（同一批資料要餵給報告引擎），
  //  只有**問法**換成鏡月的語氣：她不問「呢」，她用肯定句加一個停頓。
  relation: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    question: "你想看誰的紫微呢？",
    field: "relation",
    options: [
      { value: "self", label: "我自己" },
      { value: "family", label: "家人" },
      { value: "partner", label: "另一半" },
      { value: "friend", label: "朋友" },
      { value: "other", label: "其他" },
    ],
    next: "birthDate",
  },

  birthDate: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    question: "哪一天出生的？",
    field: "birthDate",
    input: "date",
    next: "birthTime",
  },

  //  ⚠️ 這一題在鏡月是**必填**，沒有「不知道時間」那顆開關（白夜有）。
  //
  //  note 只寫原因，不寫「不能跳過」這種規則（Kevin 2026-10-12）——
  //  畫面上本來就沒有跳過的路可以走，再講一次等於在強調限制。
  //  說清楚「為什麼需要」就夠了，那是她在解釋，不是在設門檻。
  birthTime: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    question: "幾點出生的？",
    note: "命宮要靠時辰才定得出來。",
    field: "birthTime",
    input: "time",
    next: "birthPlace",
  },

  birthPlace: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    question: "在哪裡出生的？",
    note: "出生地決定真太陽時，差一點，時辰就不同了。",
    field: "birthPlace",
    input: "place",
    confirmLabel: "就是這裡",
    next: "gender",
  },

  //  紫微的大限是男女分順逆排的 —— 性別不是用來稱呼，是排盤的輸入。
  gender: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    question: "你是男子，還是女子？",
    field: "gender",
    layout: "pill",
    options: [
      { value: "male", label: "男生" },
      { value: "female", label: "女生" },
    ],
    next: "name",
  },

  name: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    question: "我該怎麼稱呼你？",
    field: "name",
    input: "text",
    placeholder: "王大名",
    confirmLabel: "就這麼稱呼",
    next: "orientation",
  },

  //  eyebrow 是接在上一題之後的那半句 —— 讓問診讀起來像對話而不是表單。
  //  鏡月的接詞比白夜軟：她用「嗯」「我記下了」這種承接，不用「原來如此」。
  orientation: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    eyebrow: "我記下了。",
    question: "你心之所向，是異性還是同性？",
    field: "orientation",
    options: [
      { value: "hetero", label: "異性戀（Hetero）" },
      { value: "lgbt", label: "同性戀（LGBT+）" },
    ],
    next: "relationship",
  },

  relationship: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    eyebrow: "嗯。",
    question: "現在身邊有人嗎？",
    field: "relationship",
    options: [
      { value: "single", label: "單身" },
      { value: "crush", label: "有在意的人" },
      { value: "dating", label: "有對象了" },
      { value: "breakup", label: "剛分手不久" },
      { value: "married", label: "已經結婚了" },
    ],
    next: "career",
  },

  career: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    eyebrow: "我聽著。",
    question: "平日裡做些什麼？",
    field: "career",
    options: [
      { value: "student", label: "我是學生" },
      { value: "seeking", label: "正在找工作" },
      { value: "employed", label: "有在上班" },
      { value: "business", label: "自己做生意" },
      { value: "freelance", label: "我是自由職業者" },
      { value: "none", label: "目前沒有工作" },
    ],
    next: "concern",
  },

  concern: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    //  ⚠️ 這一題的四段文字都要是鏡月的語氣（溫婉、空靈、輕柔，見
    //  constant/products.js 的 persona.tone）：不催、不用驚嘆號、不帶命令。
    //
    //  「說」不是「寫」—— 她蒙著眼，接收的動作是聽，這跟三幕的
    //  「把生辰說給我聽」是同一條線。所以收尾的按鈕是「說完了」而不是「寫好了」。
    //
    //  「都好」而不是「跳過」：跳過是系統的詞，而她不會把不問當成略過一個步驟。
    question: "有什麼掛在心上的嗎？",
    field: "concern",
    input: "memo",
    placeholder: "說說看",
    skipLabel: "沒有，都好",
    doneLabel: "說完了",
    next: "email",
  },

  email: {
    visual: JINGYUE_VIDEO_3,
    blur: true,
    //  不說「留一個收信的地方」—— 那是表單在跟你要欄位。
    //  她要的是「我寫好之後送到哪裡」，收件這件事變成她的動作而不是你的義務。
    question: "寫好之後，我送到哪兒給你？",
    field: "email",
    input: "email",
    placeholder: "example@gmail.com",
    confirmLabel: "就送這裡",
    //  送出信箱就直接進故事頁 —— 她讀盤那兩幕（jingyue-4 / jingyue-5）
    //  搬到故事頁的開場去了（見 jingyue-story/jingyue-story-intro）。
    //  搬的理由是時間：故事頁一掛載就開始生成命書，那兩支影片剛好把生成藏住。
    next: STORY_PATH,
  },
};

//Email 格式檢查。
//  刻意只驗「有沒有 @、@ 後面有沒有點」這種程度 ——
//  完整照 RFC 5322 驗會是一條上百字元的正則，而且會擋掉合法但少見的地址。
//  真正確認信箱有效的方法是寄一封信過去，不是在前端用正則猜。
const isEmailValid = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());

//煩惱欄位的字數上限。留得寬一點 —— 這題本來就是要人把話講完，
//  但仍要有個頂，免得整篇小說被送進 prompt 把 token 吃光。
const CONCERN_MAX = 500;

//名字的長度上限。排盤用不到它，純粹是防止有人貼一整篇文章進來 ——
//  真名再長也不會超過這個數。
const NAME_MAX = 20;

//信箱長度上限。RFC 5321 規定的實際上限是 254 個字元。
const EMAIL_MAX = 254;

//送出去的那一包。
//  問診幕只有「被填過」的欄位才會出現在 answers 裡 —— 跳過的題目是「沒有這個 key」，
//  不是「值是空字串」。對接收端（故事頁、之後的排盤 API）來說，
//  「欄位不存在」和「使用者留白」是兩件很難分辨的事，所以在這個邊界一次補齊。
//
//  目前只有 concern 可以留白（唯一掛 skipLabel 的一題），其他題目不填就走不到出口。
const buildIntake = (answers) => ({
  ...answers,
  concern: answers.concern ?? "",
});

//順著場景表往前找「接下來第一支不一樣的影片」。
//  用來提早下載：等到換幕才開始載的話，使用者按了按鈕卻只能盯著舊畫面等，
//  那比閃一下黑的更像沒反應。
//
//  走訪時記著走過的 key —— 場景表之後若出現環（某一幕又指回前面），
//  沒有這道防線就會在這裡無限繞下去。
const nextVisualAfter = (key) => {
  const from = SCENES[key];
  if (!from) return null;

  const seen = new Set([key]);
  let cur = from;

  for (let i = 0; i < 20; i += 1) {
    const nextKey = cur.autoNext || cur.next || cur.actions?.[0]?.next;
    if (!nextKey || seen.has(nextKey)) return null;
    seen.add(nextKey);

    const nextScene = SCENES[nextKey];
    if (!nextScene) return null;
    if (nextScene.visual !== from.visual) return nextScene.visual;
    cur = nextScene;
  }
  return null;
};

const FIRST_SCENE = "gate";

//「略過故事」的落點：跳過劇情，但不跳過問診 ——
//  「要看誰的八字」是排盤必要的資料，不是故事的一部分。
const SCENE_SKIP_TO = "relation";

const ZiweiJingyuePage = () => {
  //—— 動線打點：進產品頁 ——
  //  漏斗的第一段。沒有它的話，「有多少人看了卻沒開始問診」算不出來
  //  （訂單只記得最後兩段）。track 自己會去重，StrictMode 跑兩次沒關係。
  useEffect(() => {
    track("product_view", "ziwei-jingyue");
  }, []);

  const navigate = useNavigate();

  const [sceneKey, setSceneKey] = useState(FIRST_SCENE);
  //問診幕收到的答案 —— 這些是真的要送去排盤的資料。
  //  收在一個物件裡而不是一題一個 state：之後還有時辰、性別、出生地，
  //  每加一題就要改 state、改 trail、改還原邏輯，三個地方漏一個就會出錯。
  //  用 scene.field 當 key，加題目時只要動 SCENES 那張表。
  const [answers, setAnswers] = useState({});

  //走過的路：每前進一幕就壓一份「當時的狀態」進去，返回時彈出來還原。
  //  不在 SCENES 表裡寫死 prev —— 劇情有分支，之後場景多了同一幕可能有好幾個入口，
  //  寫死的上一幕會把人送回他根本沒走過的地方。堆疊記的是實際走法，不會錯。
  const [trail, setTrail] = useState([]);

  //目前播到第幾句台詞
  const [lineIndex, setLineIndex] = useState(0);

  //標語輪到第幾句。
  //  起點隨機抽，之後「依序」往下輪 —— 每次進站都從不同一句開門，
  //  再進來的人不會覺得是同一張靜態畫面；但換句不能也隨機，
  //  純隨機會連續抽到同一句，看起來就像卡住了，依序走則保證三句都被看到。
  //
  //  長度直接從 FIRST_SCENE 取，而不是等 effect 掛上才抽：
  //  effect 在畫面畫完之後才跑，那會先閃一格第一句才跳到隨機那句。
  const [taglineIndex, setTaglineIndex] = useState(() =>
    Math.floor(Math.random() * (SCENES[FIRST_SCENE].taglines?.length || 1)),
  );

  //—— 影片與聲音 ——
  //  預設就是有聲的，但瀏覽器有最終決定權：沒有使用者互動過的頁面，
  //  帶聲音的自動播放會被擋（play() 回 NotAllowedError）。
  //  被擋就退回靜音續播、露出「開啟聲音」——
  //  畫面不能因為播不出聲音就停在那裡。
  //—— 兩層影片 ——
  //
  //  換幕換到別支影片時，舊的那支不會馬上消失：新的先在背後載入、播起來，
  //  真的動起來了才交棒，舊的在那一刻才開始淡出。
  //
  //  之前是一個 <video> 用 key={scene.visual} 重掛 —— 一換幕舊的就被卸載，
  //  新的還在載，中間畫面上什麼都沒有，那就是會閃一下的黑屏。
  //  兩層輪流用就沒有那個空窗：任何時候至少有一支是有畫面的。
  //
  //  只開兩層而不是每支一層：同時只會有「正在播的」與「正在載的」兩支，
  //  五支全掛著等於一進站就吃掉三十幾 MB。
  const videoA = useRef(null);
  const videoB = useRef(null);
  const layerRef = (i) => (i === 0 ? videoA : videoB);

  //每一層現在載著哪一支、要不要模糊。
  //  模糊記在「層」上而不是每次從 scene 重算 —— 交棒的那半秒裡，
  //  正在淡出的那一層必須維持它原本的樣子，不然會看到它一邊淡出一邊變清晰。
  const [layers, setLayers] = useState(() => [
    { src: SCENES[FIRST_SCENE].visual, blur: false },
    null,
  ]);

  //哪一層正在給人看
  const [front, setFront] = useState(0);

  //第一次露臉才跑「推近落定」那段動畫；之後換影片一律只做交叉淡入 ——
  //  每換一幕都推一次鏡太吵，而且有舊畫面墊著，本來就不需要再遮什麼。
  const [firstReveal, setFirstReveal] = useState(true);

  //已經預抓過的影片，同一支不重複抓
  const prefetched = useRef(new Set());

  const [muted, setMuted] = useState(false);
  const [soundBlocked, setSoundBlocked] = useState(false);

  //這一幕的影片播完了沒。只有 waitVideo 的幕在乎它 ——
  //  那種幕的按鈕要等影片播完才出現（見 sceneDone）。
  const [videoEnded, setVideoEnded] = useState(false);

  //影片「真的開始動了」沒。在那之前畫面是純黑的（見 scss 的 jingyue__visual）。
  //
  //  刻意不用 poster：poster 會在影片還在載的時候顯示第一格，
  //  於是變成「先看到一張靜止的圖，等半天才開始動」——
  //  那個停頓比一片黑更像當機。黑畫面是「還在載」，靜止的圖是「壞了」。
  //
  //  用 playing 而不是 canplay：canplay 只保證「可以播了」，
  //  自動播放被擋的那一瞬間它就會觸發，畫面還是靜止的 —— 又繞回同一個問題。
  const [videoPlaying, setVideoPlaying] = useState(false);

  const scene = SCENES[sceneKey];
  const isForm = Boolean(scene.question);

  //這一幕的影片現在在哪一層。null 代表還沒指派（下面第一個 effect 會補上）。
  //  它既是「要播哪一層」也是「交棒的目標」：等於 front 就是已經在演了，
  //  等於背面那層就表示正在暖身、還沒輪到它。
  const target =
    layers[front]?.src === scene.visual
      ? front
      : layers[1 - front]?.src === scene.visual
        ? 1 - front
        : null;

  //—— 字幕等畫面 ——
  //
  //  這一幕的影片真的在前景演了沒。要三個條件同時成立：
  //  已經指派到某一層（target）、那一層就是前景（交棒完成）、而且畫面真的動起來了。
  //
  //  台詞原本只看 sceneKey：換幕就從第一句起算，只延後 leadIn。
  //  但換幕是「背面那層載好、播起來、才交棒」—— 桌機快取熱的時候
  //  這段只有幾十毫秒，感覺不出來；手機上網路與解碼都慢，
  //  常常超過 leadIn，於是上一支影片（或一片黑）還在，字已經在講下一幕的話。
  const visualLive = target !== null && target === front && videoPlaying;

  //等不到畫面了：影片載壞（handleLayerError 刻意不交棒），
  //  或慢到超過 VISUAL_WAIT_MS。兩種都讓台詞照常開始 ——
  //  沒有這個出口的話，影片一壞整個故事就停在那裡不說話。
  const [visualGaveUp, setVisualGaveUp] = useState(false);
  const visualReady = visualLive || visualGaveUp;

  //這一幕的答案。所有問診幕共用這組讀寫，所以渲染邏輯不用管現在問的是哪一題。
  const answer = scene.field ? answers[scene.field] : undefined;
  //—— 動線打點：開始問診 ——
  //  掛在「第一次作答」而不是「進到第一題」：看到題目卻沒動手的人
  //  還沒真的開始。track 只會在第一次呼叫時送出，之後都是空操作。
  const setAnswer = (value) => {
    track("intake_start", "ziwei-jingyue");
    setAnswers((a) => ({ ...a, [scene.field]: value }));
  };

  //這一題可以往下了嗎。單選只要有選；日期與時間要真的成立 ——
  //  光看有沒有填的話，2 月 30 日和 25:99 都會放行。
  const canProceed =
    scene.input === "date"
      ? isBirthDateValid(answer)
      : scene.input === "time"
        ? isBirthTimeValid(answer)
        : scene.input === "email"
          ? isEmailValid(answer)
          : scene.input === "text"
            ? //名字：去掉前後空白之後還有字才算填了。
              //  只打空白鍵也能過的話，報告裡的稱呼會變成一片空白。
              Boolean(String(answer || "").trim())
            : scene.skipLabel
              ? //可以跳過的題目：留白也能往下走
                true
              : //出生地要「從清單裡選過」才算數：只打了字沒選的話，
                //答案是 null，我們也就沒有經緯度可以換算真太陽時。
                Boolean(answer);

  //按鈕現在要顯示「跳過」還是「下一步」。
  //  只有可跳過的題目、而且真的還沒寫東西時才是跳過。
  const skipping = Boolean(scene.skipLabel) && !String(answer || "").trim();

  //這一幕要說的話。用 useMemo 是為了讓底下的 effect 有穩定的依賴 ——
  //  每次 render 都產生新陣列的話，播放用的 timer 會被反覆清掉重設，台詞就永遠停在第一句。
  const lines = useMemo(
    () => (SCENES[sceneKey].lines || []).map(normalizeLine),
    [sceneKey],
  );

  //台詞播完了沒。沒有台詞的幕（gate / 問診幕）一開始就算播完。
  const linesDone = lineIndex >= lines.length;

  //這一幕演完了沒。一般的幕是台詞播完就算演完；
  //  waitVideo 的幕還要等影片播完 —— 他還在看竹冊，這時把按鈕推到面前是在催他。
  //
  //  「演完」之後會發生什麼由那一幕自己決定：出選項、自動往下走（autoNext），
  //  或是離開場景表導到別頁（exit）。三者共用這一個條件，節奏才會一致。
  const sceneDone = linesDone && (!scene.waitVideo || videoEnded);

  //畫面落定之後再過一拍（見 PANEL_DELAY_MS）。
  //  換幕就重新計時 —— 每一幕都該先讓人看到畫面。
  const [visualSettled, setVisualSettled] = useState(false);

  //底部面板要不要掛。裡面裝的是選項與「略過故事」，兩者都沒有就不掛 ——
  //  autoNext 的幕（台詞說完自己往下走）就是這種情況，掛上去只是一個空的 div。
  //
  //  有選項的幕要等台詞播完才掛：話還沒說完就讓人挑答案，等於叫他別聽了。
  //  只掛「略過故事」的幕則整幕都在：那是逃生門不是答案，不該跟著台詞的節奏出現。
  //  再加一道 visualSettled：面板裡的「略過故事」與標語不跟著台詞跑，
  //  少了它，影片還在載、畫面一片黑，按鈕就已經浮在那裡了。
  const showPanel =
    visualSettled &&
    ((sceneDone && Boolean(scene.actions)) || Boolean(scene.skip));

  //這一幕的每一句要再多停多久。換句的 timer 與台詞的動畫時長共用它，
  //  兩邊不一致的話會「字淡完了還空著」或「字還在就被換掉」。
  const lineHold = scene.lineHold || 0;

  //這一幕開口前要先安靜多久。同樣要同時餵給 timer 與 CSS 的 animation-delay。
  const leadIn = scene.leadIn ?? LEAD_IN_MS;

  //這一句最長的一段有幾個字（強制換行 \n 切開後算）。
  //  交給 CSS 去算字級，讓每一句都剛好擺得下一行 —— 見 scss 的 --line-chars。
  const lineChars = linesDone ? 0 : longestRun(lines[lineIndex].text);

  //這一句的字級上限（0 代表照 scss 的預設 42px 走）。
  //  只有表裡明講 max 的句子會拿到別的值 —— 見 normalizeLine。
  const lineMax = linesDone ? 0 : lines[lineIndex].max;

  //把「這一幕要播哪一支、糊不糊」同步到兩層上。
  //  同一支影片（問診那幾幕共用第三幕的那支）→ 只更新前景那層的模糊，不重載。
  //  換成別支 → 丟進背面那層開始載，等它真的動起來才交棒（見 handleLayerPlaying）。
  useEffect(() => {
    setLayers((prev) => {
      const cur = prev[front];
      if (cur?.src === scene.visual) {
        if (cur.blur === Boolean(scene.blur)) return prev;
        const next = [...prev];
        next[front] = { src: cur.src, blur: Boolean(scene.blur) };
        return next;
      }

      const other = prev[1 - front];
      if (other?.src === scene.visual && other.blur === Boolean(scene.blur)) {
        return prev;
      }
      const next = [...prev];
      next[1 - front] = { src: scene.visual, blur: Boolean(scene.blur) };
      return next;
    });
  }, [scene.visual, scene.blur, front]);

  //讓「該播的那一層」播起來，先用「有聲音」試。
  //  不用 <video autoPlay>：那是瀏覽器自己去播，被擋下來我們接不到、
  //  也就沒機會退回靜音。自己呼叫 play() 才拿得到那個 rejection。
  //
  //  背面那層一律靜音播（muted || i !== front）—— 它是在暖身，
  //  不靜音的話會聽到兩軌聲音疊在一起。交棒之後它就是前景，聲音自然回來。
  useEffect(() => {
    if (target === null) return;
    const video = layerRef(target).current;
    if (!video) return;

    video.muted = muted || target !== front;
    //已經有 metadata 的話（返回上一幕、影片在快取裡）在這裡跳；
    //  還沒有的話由 <video> 的 onLoadedMetadata 接手 —— 那時才 seek 得動。
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      video.currentTime = INTRO_SKIP_S;
    }
    const played = video.play();
    if (!played?.catch) return;

    played.catch((err) => {
      //play() 還沒完成就被 pause() 打斷會丟 AbortError —— 那是我們自己叫停的
      //  （問診幕要把畫面停住），不是瀏覽器擋掉聲音。
      //  不分辨的話，一進問診就會冒出一顆「開啟聲音」，而聲音根本沒被擋。
      if (err?.name === "AbortError") return;

      //被擋下來（NotAllowedError）：靜音續播，並露出「開啟聲音」讓使用者一鍵要回來
      video.muted = true;
      setMuted(true);
      setSoundBlocked(true);
      //連靜音都播不動：那就別再等 playing 了，直接把畫面放出來。
      //  不然使用者會停在一片黑上，那比看到靜止的第一格糟得多。
      video.play().catch(() => setVideoPlaying(true));
    });
    //muted 不進依賴：它變動時由下面那個 effect 同步到 DOM，
    //放進來會在每次切換靜音時重新起播影片。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, scene.visual]);

  //換影片就重算「播完了沒」。
  //  綁 scene.visual 而不是 sceneKey：連續幾幕共用同一支影片時它不會重播，
  //  跟著換幕重設的話，第二幕之後就永遠等不到 ended。
  //
  //  videoPlaying 刻意「不」跟著重設 —— 它現在的意思是「前景那層有畫面了」。
  //  換幕時重設會讓畫面先黑掉，那正是這次要修掉的東西。
  useEffect(() => {
    setVideoEnded(false);
  }, [scene.visual]);

  //靜音狀態同步到 DOM。兩層都要同步，而且背面那層一律靜音。
  //  React 對 <video muted> 的處理有已知落差，直接寫 DOM property 最保險。
  useEffect(() => {
    [0, 1].forEach((i) => {
      const video = layerRef(i).current;
      if (video) video.muted = muted || i !== front;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted, front]);

  //前景那支開始播之後，順手把「接下來第一支不一樣的影片」抓進快取。
  //  一次只抓一支：五支全抓等於替使用者決定要花掉三十幾 MB。
  //  開了資料節省模式或連線很慢就不抓 —— 那種情況下預抓只會拖慢正在看的東西。
  useEffect(() => {
    if (!videoPlaying) return;

    const next = nextVisualAfter(sceneKey);
    if (!next || prefetched.current.has(next)) return;

    const net = navigator.connection;
    if (net?.saveData) return;
    if (net?.effectiveType && /(^|-)2g$/.test(net.effectiveType)) return;

    prefetched.current.add(next);
    //body 讀掉才會進快取，讀完就讓它被回收 —— 要的是快取，不是這份資料
    fetch(next)
      .then((res) => res.blob())
      .catch(() => {});
  }, [sceneKey, videoPlaying]);

  //—— 問診時把影片停住 ——
  //
  //  模糊與暫停是同一件事的兩面，所以共用 scene.blur 這一個旗標，不另外給欄位：
  //  模糊是「別看畫面了」，暫停是「他也停下來等你」。只模糊不暫停的話，
  //  一團糊掉的色塊還在背後晃，反而比清晰的畫面更容易把眼睛拉過去。
  //
  //  問診幕沿用第三幕那支影片（JINGYUE_VIDEO_3），中途不換 ——
  //  換了的話「暫停」會停在另一支影片的某一格，那不是暫停，是換了張圖。
  //
  //  問診的每一幕都模糊，所以影片從第四幕停住之後就一路停到最後一題 ——
  //  整段問診是同一個靜止的畫面，中途動起來會像是換了個場景。
  //  else 那一支是留給劇情幕的：從問診返回劇情時要把影片接回去播。
  useEffect(() => {
    if (target === null) return;
    const video = layerRef(target).current;
    if (!video) return;

    if (scene.blur) video.pause();
    else video.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.blur, target]);

  //—— iOS 鍵盤收起後把畫面拉回原位 ——
  //
  //  iOS Safari 在鍵盤彈出時會強制捲動整個頁面，讓聚焦的輸入框露出來；
  //  但鍵盤收起之後它「不會」把頁面捲回去。
  //  這一頁是滿版固定高度、沒有可捲的內容，所以捲上去之後
  //  下半部就是一整片空白，而且再也回不來 —— 看起來像畫面卡住了。
  //
  //  監聽 focusout 而不是 blur：blur 不會冒泡，掛在容器上收不到。
  //  延遲是等鍵盤的收合動畫跑完，太早捲會被它的動畫蓋掉。
  useEffect(() => {
    const onFocusOut = (e) => {
      const tag = e.target?.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA") return;
      setTimeout(() => window.scrollTo(0, 0), 120);
    };

    window.addEventListener("focusout", onFocusOut);
    return () => window.removeEventListener("focusout", onFocusOut);
  }, []);

  //把聲音要回來。
  //  呼叫點一定是在使用者的點擊事件裡 —— 那一下就是瀏覽器要的授權，
  //  所以解除之後不會再被擋。
  const enableSound = () => {
    setMuted(false);
    setSoundBlocked(false);
    //只解除前景那層 —— 背面那層在暖身，讓它出聲會變成兩軌疊在一起
    const video = layerRef(front).current;
    if (video) {
      video.muted = false;
      video.play().catch(() => {});
    }
  };

  //—— 兩層影片的事件 ——

  //某一層真的動起來了。
  //  交棒只在這裡發生：背面那層有畫面了才換到前景，
  //  所以任何一刻畫面上都有東西，不會出現兩層都是空的那一瞬間。
  const handleLayerPlaying = (i) => {
    setVideoPlaying(true);
    if (i !== front) {
      setFront(i);
      setFirstReveal(false);
    }
  };

  //播完了。只有前景那層算數 —— 背面那層還在暖身，它的 ended 不該影響流程。
  const handleLayerEnded = (i) => {
    if (i !== front) return;

    //要等「播完」的幕：按鈕該出現了
    if (scene.waitVideo) {
      setVideoEnded(true);
      return;
    }

    //停在最後一格的幕：什麼都不做就是了 —— <video> 播完本來就停在那裡。
    //  跟 waitVideo 分開是因為兩者管的不是同一件事：
    //  waitVideo 管「按鈕什麼時候出現」，holdEnd 只管「畫面要不要回頭重播」。
    //  混成一個旗標的話，想要「台詞講完就給按鈕、但畫面演完停住」就做不到。
    if (scene.holdEnd) return;

    //其他幕自己循環 —— 回到開頭之後而不是第 0 格（見 INTRO_SKIP_S）
    const video = layerRef(i).current;
    if (!video) return;
    video.currentTime = INTRO_SKIP_S;
    video.play().catch(() => {});
  };

  //載不出來。讓等播完的幕放行（不然人會卡在這一幕出不去），
  //  但刻意「不」交棒 —— 換過去只會看到一層空的，還不如讓上一支繼續播。
  const handleLayerError = (i) => {
    if (i !== target) return;
    setVideoEnded(true);
    setVideoPlaying(true);
    //這一層永遠不會交棒了，台詞不能跟著一起卡住
    setVisualGaveUp(true);
  };

  //換幕就從第一句重播。
  useEffect(() => {
    setLineIndex(0);
  }, [sceneKey, lines]);

  //畫面出來了才開始算那一拍。換幕一律重來 ——
  //  不重設的話，下一幕的按鈕會在畫面還沒接上時就已經是顯示狀態。
  useEffect(() => {
    setVisualSettled(false);
    if (!visualReady) return undefined;
    const timer = setTimeout(() => setVisualSettled(true), PANEL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [sceneKey, visualReady]);

  //影片還沒交棒就開始倒數，逾時就放行（見 visualGaveUp）。
  //  已經在演了就不用計時 —— 也順手把上一幕留下來的「放棄」狀態清掉。
  useEffect(() => {
    if (visualLive) return undefined;
    setVisualGaveUp(false);
    const timer = setTimeout(() => setVisualGaveUp(true), VISUAL_WAIT_MS);
    return () => clearTimeout(timer);
  }, [sceneKey, visualLive]);

  //一句一句往下推。時間到了就換下一句；最後一句播完 lineIndex 會等於 lines.length，
  //  linesDone 變 true，選項才浮出來。
  //  每一句的停留時間看字數（lineDuration），不是一律 1.5 秒 ——
  //  長短句用同一個時間，長的那句會來不及讀。
  //  第一句要多等一段 LEAD_IN_MS —— 它的動畫也延後同樣的時間才開始播（見下方 inline style），
  //  兩邊不一致的話字還沒淡完就被換掉。
  //
  //  畫面還沒交棒就先不起算（visualReady）—— 計時器和底下那句的淡入動畫
  //  必須從同一個時間點開始，不然字會在影片還沒出來之前就跑掉。
  useEffect(() => {
    if (linesDone || !visualReady) return;
    const wait =
      lineDuration(lines[lineIndex].text, lineHold) +
      (lineIndex === 0 ? leadIn : 0);
    const timer = setTimeout(() => setLineIndex((i) => i + 1), wait);
    return () => clearTimeout(timer);
  }, [sceneKey, lineIndex, linesDone, lines, lineHold, leadIn, visualReady]);

  //台詞說完自己往下走（autoNext 的幕）。
  //  只用得到 setSceneKey，不走 goScene —— 這種幕不壓進走過的路（原因見 goScene），
  //  所以這裡不需要碰 trail / answers，依賴就只有「去哪」與「說完了沒」。
  useEffect(() => {
    if (!scene.autoNext || !sceneDone) return;
    const timer = setTimeout(() => setSceneKey(scene.autoNext), AUTO_NEXT_MS);
    return () => clearTimeout(timer);
  }, [scene.autoNext, sceneDone]);

  //標語輪播。只有掛了標語的幕才起計時器 —— 其他幕不該有一顆空轉的 interval。
  //  依賴放 scene.taglines：它是 SCENES 表裡的同一個陣列，每幕的參照都固定，
  //  所以重新 render 不會重設計時器；換到沒有標語的幕時計時器就被清掉。
  useEffect(() => {
    const list = scene.taglines;
    if (!list || list.length < 2) return;

    const timer = setInterval(
      () => setTaglineIndex((i) => (i + 1) % list.length),
      TAGLINE_MS,
    );
    return () => clearInterval(timer);
  }, [scene.taglines]);

  //往下一幕走。
  const goScene = (next) => {
    //—— next 給的是路徑就離開這一頁 ——
    //  問診最後一題（email）的「確認」走這條：後面那兩幕搬到故事頁了。
    //
    //  問診收到的答案跟著人一起過去（location state），不放網址參數：
    //  裡面有生日、姓名、信箱這些個資，放進網址就會留在瀏覽紀錄、
    //  被分享出去、被記進伺服器的 access log。
    //
    //  欄位名就是 SCENES 裡的 scene.field，這裡不改名也不轉換 ——
    //  例如 gender 仍然是 "male" / "female"，排盤要的 "1" / "0"
    //  留到真正要排盤時再轉。中間多轉一手，出錯時很難分辨是誰轉錯的。
    //
    //  代價是重新整理就沒了。所以故事頁那邊要擋：沒接到資料就把人送回這裡，
    //  不要讓它停在一個沒有前提的畫面上。
    if (String(next).startsWith("/")) {
      //—— 動線打點：問診完成 ——
      //  打在真的要走人之前，不是在最後一題作答時 ——
      //  他還可以回頭改答案，改到一半離開的不算走完。
      track("intake_done", "ziwei-jingyue");
      navigate(next, { state: { intake: buildIntake(answers) } });
      return;
    }

    //自己往下走的幕（autoNext）不壓進走過的路：
    //  返回時停在那裡，它只會把人再往前推一次 —— 返回鍵就等於失效。
    //  這種幕是「經過」不是「站點」，所以返回會跳過它回到上一個真的會等人的幕。
    if (!scene.autoNext) setTrail((t) => [...t, { sceneKey, answers }]);
    setSceneKey(next);
  };

  const handleAction = (action) => {
    //劇情按鈕本身就是一次使用者互動 —— 順手把被擋掉的聲音要回來。
    //  第一幕就有按鈕（「立即隨他入閣」），所以多數人按下去的當下聲音就回來了，
    //  根本不必去找右上角那顆。
    if (soundBlocked) enableSound();
    goScene(action.next);
  };

  //返回：回上一幕；已經在第一幕就離開這一頁。
  //  直接貼網址進來時 history 沒有上一頁，navigate(-1) 會把人送出本站 ——
  //  改導回首頁（退路判斷同 sub-header）。
  const handleBack = () => {
    if (trail.length === 0) {
      if (window.history.state?.idx > 0) navigate(-1);
      else navigate("/", { replace: true });
      return;
    }

    const prev = trail[trail.length - 1];
    setTrail((t) => t.slice(0, -1));
    setSceneKey(prev.sceneKey);
    //答案一起還原：回到某一題時，後面幾題填過的東西要跟著消失，
    //否則返回改了前面的答案，後面卻還留著依前一個答案填的內容。
    setAnswers(prev.answers);
  };

  //略過故事：跳過劇情，但不跳過問診（落點見 SCENE_SKIP_TO）。
  //  走 goScene 而不是自己推一份 trail —— 前進的規則只該有一份，
  //  兩個地方各寫一次，之後改了一邊就會不一致。
  //
  //  目前沒有任何一幕掛 skip: true（二幕拿掉之後），所以這顆按鈕不會出現。
  //  機制留著：之後要把逃生門掛回哪一幕，加那一行就好。
  const handleSkip = () => goScene(SCENE_SKIP_TO);

  return (
    <div className="jingyue">
      <div className="jingyue__stage">
        {/* 返回鍵放在換幕的 key 外面：它要一直待在原地，
            跟著場景重新掛載的話每切一幕就會閃一次。 */}
        <button
          type="button"
          className="jingyue__back"
          onClick={handleBack}
          aria-label="返回"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M15 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* 兩層影片疊著，只有前景那層看得見（見 scss 的 is-front）。
            換幕換到別支時，新的先在背面載入播起來，真的動起來才交棒 ——
            交棒的那半秒兩層一起淡入淡出，中間不會有空窗，也就不會閃黑。

            聲音預設是開的：影片自己帶配樂與環境音，她說話時有聲音才像有人在。
            但有聲自動播放一律會被瀏覽器擋掉，所以 muted 是由 js 那邊決定的 ——
            先用有聲試播，被擋才退回靜音並露出「開啟聲音」（見上方的 soundBlocked）。

            key 綁層的序號而不是影片網址：綁網址就會在換影片時重掛，
            那正是原本黑屏的成因。 */}
        {[0, 1].map((i) => {
          const layer = layers[i];
          return (
            <video
              key={i}
              ref={i === 0 ? videoA : videoB}
              className={`jingyue__visual${
                layer?.blur ? " jingyue__visual--blur" : ""
              }${i === front && videoPlaying ? " is-front" : ""}${
                i === front && firstReveal ? " jingyue__visual--enter" : ""
              }`}
              src={layer?.src || undefined}
              //背面那層一律靜音，不然會聽到兩軌疊在一起
              muted={muted || i !== front}
              //不能用 <video loop>：它回頭時一定是從第 0 格開始，
              //  而第 0 格正是要跳過的那一格（見 INTRO_SKIP_S）。
              //  所以循環自己接（見 handleLayerEnded）。
              playsInline
              preload="auto"
              //metadata 一到就把播放頭移到開頭之後，跳過那張靜止的第一格
              onLoadedMetadata={(e) => {
                e.currentTarget.currentTime = INTRO_SKIP_S;
              }}
              onPlaying={() => handleLayerPlaying(i)}
              onEnded={() => handleLayerEnded(i)}
              onError={() => handleLayerError(i)}
            />
          );
        })}

        {/* 只在瀏覽器擋掉有聲自動播放時才出現。
            給的是「開啟」而不是「關閉」—— 會走到這裡就代表聲音已經被迫關掉了，
            使用者需要的是要回來的入口。
            放右上角，和左上角的返回鍵分開。 */}
        {soundBlocked && (
          <button
            type="button"
            className="jingyue__sound"
            onClick={enableSound}
          >
            <span aria-hidden="true">🔇</span>
            開啟聲音
          </button>
        )}

        {/* key 綁 sceneKey：換幕時這一層重新掛載，過場動畫才會重跑。
            不這樣做的話 React 會沿用同一個 DOM 節點，animation 只在第一幕播一次。 */}
        <div className="jingyue__scene" key={sceneKey}>
          {/* 底部漸層：按鈕是半透明的，背後剛好是亮畫面時沒有這層會糊掉。
              問診幕但背景沒模糊時要更重 —— 模糊本身就壓掉了大半對比，
              沒有它的話，引子與選項這些小字會直接落在清晰的影像上。 */}
          <div
            className={`jingyue__veil${
              isForm && !scene.blur ? " jingyue__veil--strong" : ""
            }`}
          />

          {/* —— 問診幕 —— */}
          {isForm ? (
            <div className="jingyue__form">
              {/* 題目上方的引子。用來轉語氣 ——
                  「話說回來…」讓這題讀起來像他多問一句，不是表單的下一格。 */}
              {scene.eyebrow && (
                <p className="jingyue__eyebrow">{scene.eyebrow}</p>
              )}

              {/* 長題目自動縮一級。32px 在 440px 的欄寬裡大約放得下十個字，
                  再長就會折成兩行、把下面的選項擠掉。
                  用字數判斷而不是各題手動指定 —— 之後加題目不用再想這件事。 */}
              <h2
                className={`jingyue__question${
                  scene.question.length > 10 ? " jingyue__question--long" : ""
                }`}
              >
                {scene.question}
              </h2>

              {/* 題目底下的說明。只有「這題為什麼要問」不明顯時才給 ——
                  每題都掛一行說明會讓整份問診變得很囉嗦。 */}
              {scene.note && <p className="jingyue__subnote">{scene.note}</p>}

              {/* 單選題。用 radio 而不是一排 button：鍵盤可以用方向鍵切換、
                  讀屏會唸出「五個選項中的第幾個」，這些都是 button 給不了的。
                  視覺全部靠 label 畫，原生的圓鈕藏起來。 */}
              {scene.options && (
                <div
                  className={`jingyue__options${
                    scene.layout === "pill" ? " jingyue__options--pill" : ""
                  }`}
                  role="radiogroup"
                >
                  {scene.options.map((opt) => (
                    <label
                      key={opt.value}
                      className={`jingyue__option${
                        answer === opt.value ? " is-active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name={scene.field}
                        value={opt.value}
                        checked={answer === opt.value}
                        onChange={() => setAnswer(opt.value)}
                      />
                      {/* 圓鈕畫成真的元素而不是偽元素：
                          它自己當 flex 容器，圓心的點在裡面用 flex 置中 ——
                          不用去算 left / top 的偏移，也就不可能算錯。 */}
                      <span className="jingyue__radio" aria-hidden="true" />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* 日期題。type 是 text 而不是 number ——
                  number 會冒出上下微調鈕、也吃不了斜線；
                  inputMode="numeric" 才是「手機請給我數字鍵盤」的正確講法。 */}
              {scene.input === "date" && (
                <div className="jingyue__field">
                  <div className="jingyue__field-row">
                    <input
                      className="jingyue__date"
                      type="text"
                      //手機要跳純數字鍵盤，這兩個缺一不可：
                      //  inputMode 決定 Android 給哪種鍵盤，
                      //  pattern 則是 iOS 判斷「要不要給無字母鍵台」的依據。
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="bday"
                      placeholder="2026/01/01"
                      value={answer || ""}
                      onChange={(e) =>
                        setAnswer(formatBirthDate(e.target.value))
                      }
                    />

                    {/* 只是標示這裡收的是國曆，不是可以切換的開關 ——
                        所以用 span 不用 button。排盤本來就只吃國曆，
                        給一個按不動的按鈕反而會讓人一直想點它。 */}
                    <span className="jingyue__tag">國曆</span>
                  </div>

                  {/* 只在「八碼填滿但日子不存在」時出現。
                      填到一半就開始糾正很煩人，而按鈕一直按不下去卻不說原因更煩。 */}
                  {isBirthDateComplete(answer) && !isBirthDateValid(answer) && (
                    <p className="jingyue__hint">沒有這一天，再想想</p>
                  )}
                </div>
              )}

              {/* 時間題。作法同日期：數字鍵盤直接打，冒號由程式補。
                  ⚠️ 沒有「不知道」—— 紫微缺了時辰定不出命宮（見檔案上方的說明）。 */}
              {scene.input === "time" && (
                <div className="jingyue__field">
                  <div className="jingyue__field-row">
                    <input
                      className="jingyue__date"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="12:30"
                      value={answer || ""}
                      onChange={(e) =>
                        setAnswer(formatBirthTime(e.target.value))
                      }
                    />
                  </div>

                  {isBirthTimeComplete(answer) && !isBirthTimeValid(answer) && (
                    <p className="jingyue__hint">沒有這個時辰，再想想</p>
                  )}
                </div>
              )}

              {/* 自由填寫（煩惱）。唯一有框的欄位，也是唯一可以留白的一題。 */}
              {scene.input === "memo" && (
                <div className="jingyue__field">
                  <textarea
                    className="jingyue__memo"
                    rows={2}
                    maxLength={CONCERN_MAX}
                    placeholder={scene.placeholder}
                    value={answer || ""}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </div>
              )}

              {/* 純文字題（名字、信箱）。和出生地共用「欄位 + 確認」的並排版型。 */}
              {(scene.input === "text" || scene.input === "email") && (
                <div className="jingyue__field">
                  <div className="jingyue__field-row">
                    <input
                      className={`jingyue__text${
                        scene.input === "email" ? " jingyue__text--email" : ""
                      }`}
                      //type="email" 讓手機鍵盤直接給 @ 與 .com ——
                      //  信箱要打的符號在一般鍵盤上都要切頁面找。
                      type={scene.input === "email" ? "email" : "text"}
                      inputMode={scene.input === "email" ? "email" : undefined}
                      maxLength={scene.input === "email" ? EMAIL_MAX : NAME_MAX}
                      autoComplete={scene.input === "email" ? "email" : "name"}
                      //信箱大小寫不敏感，但自動大寫會讓人以為打錯了
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder={scene.placeholder}
                      value={answer || ""}
                      onChange={(e) => setAnswer(e.target.value)}
                    />

                    <button
                      type="button"
                      className={`jingyue__toggle${canProceed ? " is-active" : ""}`}
                      disabled={!canProceed}
                      onClick={() => goScene(scene.next)}
                    >
                      {scene.confirmLabel}
                    </button>
                  </div>

                  {/* 打了一段還不成立才提醒。剛打兩個字就說「格式不對」很煩，
                      所以等到有 @ 之後才開始挑剔。 */}
                  {scene.input === "email" &&
                    String(answer || "").includes("@") &&
                    !isEmailValid(answer) && (
                      <p className="jingyue__hint">這個地址，我送不到</p>
                    )}
                </div>
              )}

              {/* 出生地。搜尋、候選清單、debounce 都收在這支共用元件裡 ——
                  鏡月與緋紅之後同樣要問這題。
                  右邊的「確認」是這一幕的主按鈕（見下方 confirmLabel）。 */}
              {scene.input === "place" && (
                <div className="jingyue__field">
                  <div className="jingyue__field-row">
                    <div className="jingyue__field-grow">
                      <BirthplaceField
                        value={answer || null}
                        onChange={(place) => setAnswer(place)}
                      />
                    </div>

                    <button
                      type="button"
                      className={`jingyue__toggle${answer ? " is-active" : ""}`}
                      disabled={!canProceed}
                      onClick={() => goScene(scene.next)}
                    >
                      {scene.confirmLabel}
                    </button>
                  </div>
                </div>
              )}

              {/* 沒填好就不能往下 —— 這些題目沒有預設答案，替使用者猜一個會寫錯資料。
                  出生地那一幕的主按鈕做在欄位旁邊（confirmLabel），這裡就不再出現。 */}
              {!scene.confirmLabel && (
                <button
                  type="button"
                  //可跳過的題目留白時用安靜的樣式：那一下不是「完成」而是「算了」，
                  //做得和其他主按鈕一樣顯眼會變成在慫恿人跳過。
                  className={`jingyue__btn${
                    skipping ? " jingyue__btn--quiet" : " jingyue__btn--primary"
                  }`}
                  disabled={!canProceed}
                  onClick={() => goScene(scene.next)}
                >
                  {skipping ? scene.skipLabel : scene.doneLabel || "接著"}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* 台詞浮在畫面正中央，一次只顯示一句。
                  key 綁 lineIndex —— 換句時重新掛載，浮現／抽離的動畫才會重播。
                  pointer-events: none：這層鋪滿整個舞台，不讓它擋住底下的按鈕。 */}
              {!linesDone && visualReady && (
                <div className="jingyue__center">
                  <p
                    //字級由 --line-chars 交給 CSS 算（見 scss 的 jingyue__line）：
                    //  每一句各自縮到「剛好擺得下一行」，所以不會折行。
                    //  長句自然比短句小 —— 長的是鋪陳、短的是收尾，
                    //  字級差本身就在幫忙分這兩種語氣。
                    //
                    //  不在這裡算出 px：算式要用到欄寬，寫成 CSS 的 min()/calc()
                    //  可以跟著視窗走，不必在 JS 量一次寬度再監聽 resize。
                    className="jingyue__line"
                    key={lineIndex}
                    //動畫時長跟著字數走，和上面那顆 timer 吃同一個 lineDuration ——
                    //  兩邊不一致的話會「字淡完了還空著」或「字還在就被換掉」。
                    //第一句另外延後開口，讓場景先落定。animation 是 both fill mode，
                    //  延遲期間套用 0% 的狀態（opacity: 0），所以不會先閃一下才淡入。
                    style={{
                      "--line-chars": lineChars,
                      //只有表裡明講 max 的句子帶這個上限（見 normalizeLine）。
                      //  其餘的句子不帶，scss 的 var() 退路就會接手。
                      ...(lineMax ? { "--line-max": `${lineMax}px` } : null),
                      animationDuration: `${lineDuration(
                        lines[lineIndex].text,
                        lineHold,
                      )}ms`,
                      ...(lineIndex === 0
                        ? { animationDelay: `${leadIn}ms` }
                        : null),
                    }}
                  >
                    {lines[lineIndex].text}
                  </p>
                </div>
              )}

              {/* 底部面板：選項與「略過故事」（掛不掛的條件見上方 showPanel）。 */}
              {showPanel && (
                <div className="jingyue__panel">
                  {/* 按鈕上方的標語，一句換一句（內容見 SCENES 的 taglines）。
                      key 綁 taglineIndex：換句時整顆重新掛載，淡入淡出才會重播。

                      整顆膠囊一起淡掉再淡回來，而不是只換裡面的字 ——
                      膠囊的寬度是跟著字長跑的，底留著不動的話，換句的那一瞬間
                      會看到一個空膠囊自己縮一下又撐開。 */}
                  {scene.taglines && (
                    <p className="jingyue__tagline" key={taglineIndex}>
                      <span
                        className="jingyue__tagline-dot"
                        aria-hidden="true"
                      />
                      {scene.taglines[taglineIndex]}
                    </p>
                  )}

                  {scene.actions && sceneDone && (
                    //sceneDone 這裡要再檢一次：只掛「略過故事」的幕會在台詞還在播時
                    //  就掛上面板，那時選項不該跟著出現。
                    <div className="jingyue__actions">
                      {scene.actions.map((action) => (
                        <button
                          key={action.label}
                          type="button"
                          className={
                            action.image
                              ? "jingyue__btn jingyue__btn--image"
                              : "jingyue__btn"
                          }
                          //圖片按鈕的字是畫在圖上的，讀屏讀不到 ——
                          //  用 label 補一個無障礙名稱，不然它會變成「未命名按鈕」
                          aria-label={action.image ? action.label : undefined}
                          onClick={() => handleAction(action)}
                        >
                          {action.image ? (
                            <img
                              className="jingyue__btn-img"
                              src={action.image}
                              alt=""
                              //外層的 aria-label 已經說明這顆按鈕是什麼，
                              //  圖片自己再報一次會變成讀兩遍
                              aria-hidden="true"
                            />
                          ) : (
                            action.label
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 略過故事：第二次以後進來的人不該被同一段劇情擋住。 */}
                  {scene.skip && (
                    <button
                      type="button"
                      className="jingyue__skip"
                      onClick={handleSkip}
                    >
                      略過故事
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZiweiJingyuePage;
